import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/superbackend`;
const getH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('sellmu_super_token')}` } });
const fmtIDR = (v) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(v || 0);

export default function SuperPackagesPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [featureInput, setFeatureInput] = useState('');
  const [form, setForm] = useState({ name: '', store_limit: 1, price_idr: 0, description: '', features: [], is_active: true });

  useEffect(() => { loadPackages(); }, []);

  const loadPackages = async () => {
    try { const res = await axios.get(`${API}/packages`, getH()); setPackages(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, store_limit: Number(form.store_limit), price_idr: Number(form.price_idr) };
      if (editing) { await axios.put(`${API}/packages/${editing.id}`, data, getH()); }
      else { await axios.post(`${API}/packages`, data, getH()); }
      toast.success('Berhasil');
      closeDialog();
      loadPackages();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };

  const handleEdit = (pkg) => {
    setEditing(pkg);
    setForm({ name: pkg.name, store_limit: pkg.store_limit, price_idr: pkg.price_idr, description: pkg.description || '', features: pkg.features || [], is_active: pkg.is_active !== false });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false); setEditing(null); setFeatureInput('');
    setForm({ name: '', store_limit: 1, price_idr: 0, description: '', features: [], is_active: true });
  };

  const handleDelete = async () => {
    try { await axios.delete(`${API}/packages/${deleteId}`, getH()); toast.success('Dihapus'); setDeleteId(null); loadPackages(); }
    catch (err) { toast.error('Gagal'); }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm({ ...form, features: [...form.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (idx) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="super-packages-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Package Management</h1>
          <p className="text-sm text-[#64584F]">{packages.length} packages</p>
        </div>
        <Button onClick={() => { closeDialog(); setDialogOpen(true); }} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="add-package-btn">
          <Plus size={16} className="mr-2" /> Add Package
        </Button>
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">Store Limit</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">Price (IDR)</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">Features</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#A6A2A0]">Loading...</TableCell></TableRow>
            ) : packages.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#A6A2A0]">No packages yet</TableCell></TableRow>
            ) : packages.map(pkg => (
              <TableRow key={pkg.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`super-package-row-${pkg.id}`}>
                <TableCell className="font-medium text-[#202020]">{pkg.name}</TableCell>
                <TableCell className="text-[#64584F]">{pkg.store_limit} store{pkg.store_limit > 1 ? 's' : ''}</TableCell>
                <TableCell className="text-[#EB7B44] font-semibold">{fmtIDR(pkg.price_idr)}</TableCell>
                <TableCell className="hidden md:table-cell">
                  <div className="flex flex-wrap gap-1">{(pkg.features || []).slice(0, 3).map((f, i) => (
                    <Badge key={i} className="bg-[#F6F5F5] text-[#64584F] border border-[#DFDEDE] text-xs">{f}</Badge>
                  ))}{(pkg.features || []).length > 3 && <Badge className="bg-[#F6F5F5] text-[#A6A2A0] border border-[#DFDEDE] text-xs">+{pkg.features.length - 3}</Badge>}</div>
                </TableCell>
                <TableCell>
                  <Badge className={`text-xs border ${pkg.is_active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(pkg)}><Pencil size={15} className="text-[#64584F]" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(pkg.id)}><Trash2 size={15} className="text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>{editing ? 'Edit' : 'Add'} Package</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#202020]">Nama Paket</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="border-[#CACACC]" data-testid="package-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">Limit Store</Label>
                <Input type="number" min="1" value={form.store_limit} onChange={e => setForm({...form, store_limit: e.target.value})} required className="border-[#CACACC]" data-testid="package-limit-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#202020]">Harga (IDR)</Label>
                <Input type="number" min="0" value={form.price_idr} onChange={e => setForm({...form, price_idr: e.target.value})} required className="border-[#CACACC]" data-testid="package-price-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">Deskripsi</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border-[#CACACC]" data-testid="package-desc-input" />
            </div>
            {/* Features */}
            <div className="space-y-2">
              <Label className="text-[#202020] font-semibold">Features</Label>
              <div className="flex gap-2">
                <Input value={featureInput} onChange={e => setFeatureInput(e.target.value)} placeholder="e.g. Multi-store" className="border-[#CACACC] flex-1"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
                  data-testid="package-feature-input"
                />
                <Button type="button" variant="outline" onClick={addFeature} className="border-[#CACACC]">+</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.features.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-[#F6F5F5] border border-[#DFDEDE] rounded-full text-sm text-[#202020]">
                    {f}
                    <button type="button" onClick={() => removeFeature(i)} className="text-[#A6A2A0] hover:text-red-500"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={val => setForm({...form, is_active: val})} data-testid="package-active-switch" />
              <Label className="text-[#202020]">Aktif</Label>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} className="border-[#CACACC]">Batal</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="package-save-btn">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">Hapus paket ini?</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">Batal</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="super-confirm-delete-package">Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
